import React, { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { theme } from '../../theme';
import { CATEGORIES, AREAS } from '../../constants';
import { useApi } from '../../hooks/useApi';

const STEPS = ['Flokkur', 'Upplýsingar', 'Skrá', 'Staðfesta'];

export function NewRequestScreen({ onNavigate }) {
  const { isAuthenticated, login } = useAuth();
  const api = useApi();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    category: '',
    title: '',
    description: '',
    quantity: 1,
    material_preference: '',
    color_preference: '',
    area: '',
    deadline: '',
    file: null,
    image: null,
  });

  if (!isAuthenticated) {
    return (
      <div style={{ padding: '16px', textAlign: 'center', marginTop: '40px' }}>
        <p style={{ fontSize: '40px', marginBottom: '16px' }}>🔒</p>
        <p style={{ color: theme.text, fontSize: '16px', fontWeight: 600, marginBottom: '8px' }}>
          Skráðu þig inn til að senda beiðni
        </p>
        <button
          onClick={() => login()}
          style={{
            marginTop: '12px',
            background: theme.accent,
            color: '#000',
            border: 'none',
            padding: '12px 24px',
            borderRadius: theme.radiusSm,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Skrá inn
        </button>
      </div>
    );
  }

  const update = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      let fileKey = null;

      // Upload file first if present
      if (form.file) {
        const uploadRes = await api.files.upload(form.file);
        fileKey = uploadRes.data?.key || uploadRes.key;
      }

      // Determine if file is a model (STL etc.) or image
      const isImage = form.file && /\.(jpg|jpeg|png|webp|gif)$/i.test(form.file.name);

      await api.requests.create({
        category: form.category,
        title: form.title,
        description: form.description,
        quantity: form.quantity,
        material_preference: form.material_preference || null,
        color_preference: form.color_preference || null,
        area: form.area || null,
        deadline: form.deadline || null,
        file_url: fileKey && !isImage ? fileKey : null,
        image_url: fileKey && isImage ? fileKey : null,
      });

      onNavigate('browse');
    } catch (err) {
      setError(err.message || 'Villa kom upp við að senda beiðni');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ padding: '16px' }}>
      <h2 style={{ fontSize: '18px', fontWeight: 700, color: theme.text, marginBottom: '4px' }}>
        Ný beiðni
      </h2>
      <p style={{ fontSize: '13px', color: theme.textSecondary, marginBottom: '20px' }}>
        Lýstu því sem þú vilt láta búa til
      </p>

      {/* Step Indicator */}
      <div style={{
        display: 'flex',
        gap: '4px',
        marginBottom: '24px',
      }}>
        {STEPS.map((s, i) => (
          <div key={s} style={{
            flex: 1,
            height: '3px',
            borderRadius: '2px',
            background: i <= step ? theme.accent : theme.border,
            transition: 'background 0.2s',
          }} />
        ))}
      </div>

      {/* Step 0: Category */}
      {step === 0 && (
        <div>
          <p style={{ fontSize: '15px', fontWeight: 600, color: theme.text, marginBottom: '12px' }}>
            Hvaða tegund þjónustu þarftu?
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
            {CATEGORIES.map(cat => (
              <button
                key={cat.id}
                onClick={() => { update('category', cat.id); setStep(1); }}
                style={{
                  background: form.category === cat.id ? theme.accentBg : theme.bgCard,
                  border: `1px solid ${form.category === cat.id ? theme.accent : theme.border}`,
                  borderRadius: theme.radius,
                  padding: '16px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <span style={{ fontSize: '24px' }}>{cat.icon}</span>
                <span style={{ fontSize: '14px', color: theme.text, fontWeight: 500 }}>
                  {cat.label}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 1: Details */}
      {step === 1 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={labelStyle}>Titill *</label>
            <input
              value={form.title}
              onChange={e => update('title', e.target.value)}
              placeholder="T.d. LEGO kubbur, sími-hulstur, skrúfa..."
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Lýsing *</label>
            <textarea
              value={form.description}
              onChange={e => update('description', e.target.value)}
              placeholder="Lýstu því sem þú vilt láta búa til. Mál, efni, litur, séróskir..."
              rows={4}
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Fjöldi</label>
              <input
                type="number"
                min="1"
                value={form.quantity}
                onChange={e => update('quantity', parseInt(e.target.value) || 1)}
                style={inputStyle}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Svæði</label>
              <select
                value={form.area}
                onChange={e => update('area', e.target.value)}
                style={inputStyle}
              >
                <option value="">Veldu svæði</option>
                {AREAS.map(a => (
                  <option key={a.id} value={a.id}>{a.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Efni (valfrjálst)</label>
              <input
                value={form.material_preference}
                onChange={e => update('material_preference', e.target.value)}
                placeholder="T.d. PLA, tré, málmur..."
                style={inputStyle}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Litur (valfrjálst)</label>
              <input
                value={form.color_preference}
                onChange={e => update('color_preference', e.target.value)}
                placeholder="T.d. svartur, hvítur..."
                style={inputStyle}
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
            <button onClick={() => setStep(0)} style={backBtnStyle}>Til baka</button>
            <button
              onClick={() => setStep(2)}
              disabled={!form.title || !form.description}
              style={{
                ...nextBtnStyle,
                opacity: !form.title || !form.description ? 0.5 : 1,
              }}
            >
              Áfram
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Files */}
      {step === 2 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <p style={{ fontSize: '15px', fontWeight: 600, color: theme.text }}>
            Hlaðið upp skrá eða mynd (valfrjálst)
          </p>
          <div style={{
            border: `2px dashed ${theme.border}`,
            borderRadius: theme.radius,
            padding: '32px',
            textAlign: 'center',
          }}>
            <p style={{ fontSize: '32px', marginBottom: '8px' }}>📁</p>
            <p style={{ fontSize: '14px', color: theme.textSecondary }}>
              STL, 3MF, OBJ, PDF, JPG, PNG
            </p>
            <input
              type="file"
              accept=".stl,.3mf,.obj,.pdf,.jpg,.jpeg,.png,.webp"
              onChange={e => update('file', e.target.files[0])}
              style={{ marginTop: '12px' }}
            />
          </div>
          {form.file && (
            <p style={{ fontSize: '13px', color: theme.success }}>
              Skrá: {form.file.name} ({(form.file.size / 1024).toFixed(0)} KB)
            </p>
          )}
          <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
            <button onClick={() => setStep(1)} style={backBtnStyle}>Til baka</button>
            <button onClick={() => setStep(3)} style={nextBtnStyle}>Áfram</button>
          </div>
        </div>
      )}

      {/* Step 3: Confirm */}
      {step === 3 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <p style={{ fontSize: '15px', fontWeight: 600, color: theme.text }}>
            Staðfestu beiðnina
          </p>
          <div style={{
            background: theme.bgCard,
            border: `1px solid ${theme.border}`,
            borderRadius: theme.radius,
            padding: '16px',
          }}>
            <div style={summaryRow}>
              <span style={summaryLabel}>Flokkur</span>
              <span style={summaryValue}>
                {CATEGORIES.find(c => c.id === form.category)?.icon}{' '}
                {CATEGORIES.find(c => c.id === form.category)?.label}
              </span>
            </div>
            <div style={summaryRow}>
              <span style={summaryLabel}>Titill</span>
              <span style={summaryValue}>{form.title}</span>
            </div>
            <div style={summaryRow}>
              <span style={summaryLabel}>Fjöldi</span>
              <span style={summaryValue}>{form.quantity}</span>
            </div>
            {form.material_preference && (
              <div style={summaryRow}>
                <span style={summaryLabel}>Efni</span>
                <span style={summaryValue}>{form.material_preference}</span>
              </div>
            )}
            {form.file && (
              <div style={summaryRow}>
                <span style={summaryLabel}>Skrá</span>
                <span style={summaryValue}>{form.file.name}</span>
              </div>
            )}
          </div>

          {error && (
            <div style={{
              background: `${theme.error}15`,
              border: `1px solid ${theme.error}`,
              borderRadius: theme.radiusSm,
              padding: '10px 14px',
              color: theme.error,
              fontSize: '13px',
            }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
            <button onClick={() => setStep(2)} disabled={submitting} style={backBtnStyle}>Til baka</button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              style={{
                ...nextBtnStyle,
                opacity: submitting ? 0.6 : 1,
                cursor: submitting ? 'wait' : 'pointer',
              }}
            >
              {submitting ? 'Sendi...' : 'Senda beiðni'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Shared styles
const labelStyle = {
  fontSize: '13px',
  fontWeight: 600,
  color: theme.textSecondary,
  display: 'block',
  marginBottom: '6px',
};

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  background: theme.bgInput,
  border: `1px solid ${theme.border}`,
  borderRadius: theme.radiusSm,
  color: theme.text,
  fontSize: '14px',
  outline: 'none',
};

const backBtnStyle = {
  flex: 1,
  padding: '12px',
  background: theme.bgCard,
  border: `1px solid ${theme.border}`,
  borderRadius: theme.radiusSm,
  color: theme.text,
  fontWeight: 600,
  cursor: 'pointer',
};

const nextBtnStyle = {
  flex: 1,
  padding: '12px',
  background: theme.accent,
  color: '#000',
  border: 'none',
  borderRadius: theme.radiusSm,
  fontWeight: 700,
  cursor: 'pointer',
};

const summaryRow = {
  display: 'flex',
  justifyContent: 'space-between',
  padding: '8px 0',
  borderBottom: `1px solid ${theme.border}`,
};

const summaryLabel = {
  fontSize: '13px',
  color: theme.textSecondary,
};

const summaryValue = {
  fontSize: '14px',
  color: theme.text,
  fontWeight: 500,
};
